import { ProductDimensionsStoryblok } from '../../../types/generated-storyblok';
import { UnitsOfMeasure } from '../../../types/Locations';
import { isStoryblokRichTextPresent } from '../../../utils/renderStoryblokRichText';
import { getDimensionsFormat } from './getDimensionsFormat';

type DimensionsFormat = ReturnType<typeof getDimensionsFormat>;

const hasRenderableDimensions = (
  format: DimensionsFormat | null,
): format is DimensionsFormat =>
  Boolean(format?.displayString) ||
  isStoryblokRichTextPresent(format?.displayStringAlt);

/**
 * Picks the Storyblok dimension set that matches the current frame selection.
 * Unframed and framed are never used as stand-ins for each other.
 * Default (and digital) fill in when the selected physical set is empty.
 */
export const getActiveDimensionsFormat = (
  dimensions: ProductDimensionsStoryblok | undefined,
  unit: UnitsOfMeasure,
  isFramed: boolean,
): DimensionsFormat | null => {
  if (!dimensions) return null;

  const framed = dimensions.framed?.[0]
    ? getDimensionsFormat(dimensions.framed[0], unit)
    : null;
  const unframed = dimensions.unframed?.[0]
    ? getDimensionsFormat(dimensions.unframed[0], unit)
    : null;
  const defaultDims = dimensions.default?.[0]
    ? getDimensionsFormat(dimensions.default[0], unit)
    : null;
  const digital = dimensions.digital?.[0]
    ? getDimensionsFormat(dimensions.digital[0], 'PX')
    : null;

  const candidates = isFramed
    ? [framed, defaultDims, digital]
    : [unframed, defaultDims, digital];

  return candidates.find(hasRenderableDimensions) ?? null;
};

export const getActiveDimensionsLabel = (
  dimensions: ProductDimensionsStoryblok | undefined,
  unit: UnitsOfMeasure,
  isFramed: boolean,
): string | null =>
  getActiveDimensionsFormat(dimensions, unit, isFramed)?.displayString || null;
